import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { FaEye } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaDownload } from "react-icons/fa";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PurchasingInvoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState(0);
  const [totalBalanceAmount, setTotalBalanceAmount] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("invoice_code");

  useEffect(() => {
    fetchInvoices();
  }, []);

const fetchInvoices = async () => {
  const token = localStorage.getItem("token");

  try {
    const res = await axios.get(`${API_BASE_URL}/grnInvoices/get`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = res.data?.grnInvoices || []; 

    setInvoices(data);

    // calculate totals
    const totalAmount = data.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    const totalBalance = data.reduce((sum, inv) => sum + (inv.balance || 0), 0);
    setTotalInvoiceAmount(totalAmount);
    setTotalBalanceAmount(totalBalance);
  } catch (err) {
    console.error("Fetch Invoices Error:", err);
    toast.error("Failed to fetch GRN invoices.");
    setInvoices([]); // fallback to prevent filter crash
  }
};

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredInvoices = invoices.filter((inv) => {
    const fieldValue = (inv[selectedSearchField] || "").toString().toLowerCase();

    const matchSearch = normalizedSearch === "" || fieldValue.includes(normalizedSearch);

    const createdAt = new Date(inv.createdAt);
    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;

    const withinDateRange =
      (!from || createdAt >= from) && (!to || createdAt <= to);

    return matchSearch && withinDateRange;
  });

  const handleDownloadExcel = async () => {
    const dataToExport = filteredInvoices; // filtered invoices with current search & date filters

    if (dataToExport.length === 0) {
      toast.warn("No invoice data available to download.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Purchasing Invoices");

    // Header row
    const headerRow = [
      "Invoice Code",
      "GRN Code",
      "Supplier",
      "Location",
      "Amount (Rs.)",
      "Balance (Rs.)",
      "Due Date",
    ];
    worksheet.addRow(headerRow);

    // Style header
    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add data rows
    dataToExport.forEach((invoice) => {
      worksheet.addRow([
        invoice.invoice_code,
        invoice.grn_code,
        invoice.supplier_name,
        invoice.location_name,
        invoice.amount ? Number(invoice.amount).toFixed(2) : "",
        invoice.balance ? Number(invoice.balance).toFixed(2) : "",
        invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "",
      ]);
    });

    // Set column widths
    worksheet.columns.forEach((column) => {
      const maxLength = column.values.reduce((max, val) => {
        const length = val ? val.toString().length : 0;
        return length > max ? length : max;
      }, 10);
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "purchasing_invoices.xlsx");
  };


  const columns = [
    { field: "invoice_code", headerName: "Invoice Code", flex: 1 },
    { field: "createdAt", headerName: "Created At", flex: 1,
      renderCell: (params) => (
        <span>
          {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
        </span>
      ),
    },
    { field: "grn_code", headerName: "GRN Code", flex: 1 },
    { field: "supplier_name", headerName: "Supplier", flex: 1 },
    { field: "location_name", headerName: "Location", flex: 1 },
    {
      field: "amount",
      headerName: "Amount (Rs.)",
      flex: 1,
      renderCell: (params) => `Rs. ${Number(params.row.amount).toFixed(2)}`,
    },
    {
      field: "balance",
      headerName: "Balance (Rs.)",
      flex: 1,
      renderCell: (params) => `Rs. ${Number(params.row.balance).toFixed(2)}`,
    },
    {
      field: "due_date",
      headerName: "Due Date",
      flex: 1,
      renderCell: (params) => new Date(params.row.due_date).toLocaleDateString(),
    },
    { field: "created_by", headerName: "Created By", flex: 1,
      renderCell: (params) => (
        <span>{params.value ? params.value.name || params.value.username || "N/A" : "N/A"}</span>
      ),
    },
    {
        field: "action",
        headerName: "Action",
        flex: 0.5,
        renderCell: (params) => (
            <button
            className="bg-[#FBC02D] text-white p-2 rounded"
            onClick={() => setSelectedInvoice(params.row)}
            title="View Invoice"
            >
            <FaEye size={18} />
            </button>
        ),
    }
  ];

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-green-700 mb-6">Purchasing Invoices</h1>

      <div className="bg-white p-4 rounded shadow mb-6">
        <div className="flex flex-col space-y-4 mb-4">
          {/* Top row: Filters and Download button */}
          <div className="flex justify-between items-center">
            {/* Left side: Filters and summary */}
            <div className="flex items-center flex-wrap gap-4">
              <div>
                <select
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  value={selectedSearchField}
                  onChange={(e) => setSelectedSearchField(e.target.value)}
                >
                  <option value="invoice_code">Invoice Code</option>
                  <option value="grn_code">GRN Code</option>
                  <option value="supplier_name">Supplier</option>
                  <option value="location_name">Location</option>
                </select>
              </div>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Invoices"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-lg"
                />
              </div>

              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setSearchTerm("");
                }}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Clear
              </button>

              <div className="flex items-center space-x-2">
                <label className="text-lg font-semibold">Total Invoice Amount (LKR):</label>
                <span className="text-lg font-bold text-green-700">
                  {totalInvoiceAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-lg font-semibold">Total Outstanding Amount (LKR):</label>
                <span className="text-lg font-bold text-red-600">
                  {totalBalanceAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>

            {/* Right side: Download button */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>
        </div>

      </div>

      <div className="bg-white p-4 rounded shadow">
        <DataGrid
          autoHeight
          rows={filteredInvoices}
          columns={columns}
          getRowId={(row) => row._id}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>

      {selectedInvoice && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setSelectedInvoice(null)}
              className="absolute top-2 right-2 bg-red-600 text-white square-full w-8 h-8 text-xl font-bold flex items-center justify-center cursor-pointer"
            >
              &times;
            </button>
            <h2 className="text-2xl font-semibold mb-4">Invoice Details</h2>
            <p><strong>Invoice Code:</strong> {selectedInvoice.invoice_code}</p>
            <p><strong>GRN Code:</strong> {selectedInvoice.grn_code}</p>
            <p><strong>Supplier:</strong> {selectedInvoice.supplier_name}</p>
            <p><strong>Location:</strong> {selectedInvoice.location_name}</p>
            <p><strong>Due Date:</strong> {new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
            <p><strong>Amount:</strong> Rs. {selectedInvoice.amount.toFixed(2)}</p>
            <p><strong>Balance:</strong> Rs. {selectedInvoice.balance.toFixed(2)}</p>

            <h3 className="text-lg font-semibold mt-4 mb-2">Invoiced Products</h3>
            <DataGrid
              autoHeight
              rows={selectedInvoice.product_items.map((item, idx) => ({
                id: idx + 1,
                ...item,
              }))}
              columns={[
                { field: "product_name", headerName: "Product", flex: 2 },
                { field: "quantity", headerName: "Qty", flex: 1 },
                {
                  field: "unit_price",
                  headerName: "Unit Price (Rs.)",
                  flex: 1,
                  renderCell: (params) =>
                    `Rs. ${Number(params.row.unit_price).toFixed(2)}`,
                },
                {
                  field: "total",
                  headerName: "Total (Rs.)",
                  flex: 1,
                  renderCell: (params) =>
                    `Rs. ${(params.row.unit_price * params.row.quantity).toFixed(2)}`,
                },
              ]}
              hideFooter
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchasingInvoice;
